import { useState, type FormEvent } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { ArrowRight, Layers3 } from "lucide-react"
import { toast } from "sonner"
import type { MembershipSummary } from "@vienna/contracts"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { AuthLayout, AuthSwitch } from "@/components/auth/auth-layout"
import { describeAuthError } from "@/lib/auth-errors"
import { Field } from "@/components/workspace/primitives"
import { api, ApiError } from "@/lib/api"
import { authClient } from "@/lib/auth-client"
import { meQuery, rememberWorkspace } from "@/lib/queries"

export const Route = createFileRoute("/create-workspace")({ component: CreateWorkspace })

/** Screen 02. Creates the account when nobody is signed in, then the workspace; signed-in users only name the workspace. */
function CreateWorkspace() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const me = useQuery({ ...meQuery, retry: false })
  const signedIn = me.isSuccess
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [workspace, setWorkspace] = useState("")
  const [agreed, setAgreed] = useState(false)
  const [error, setError] = useState("")
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError("")
    if (!signedIn && password.length < 8) return setError("Use at least 8 characters for your password.")
    if (!signedIn && !agreed) return setError("Agree to the terms to create your workspace.")
    setBusy(true)
    try {
      if (!signedIn) {
        const result = await authClient.signUp.email({ email, password, name })
        if (result.error) throw result.error
      }
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
      const created = await api<MembershipSummary>("/workspaces", { method: "POST", body: JSON.stringify({ name: workspace, timezone }) })
      await queryClient.invalidateQueries({ queryKey: ["me"] })
      rememberWorkspace(created.workspace.slug)
      toast.success(`${created.workspace.name} is ready to set up`)
      await navigate({ to: "/w/$workspace/getting-started", params: { workspace: created.workspace.slug } })
    } catch (err) {
      setError(err instanceof ApiError ? err.problem.title : describeAuthError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthLayout
      mark={<Layers3 size={23} />}
      title="Create your workspace"
      subtitle={signedIn ? `Signed in as ${me.data.email}. Name the new workspace to continue.` : "Start with your team. You can connect channels next."}
      footer={
        signedIn ? (
          <AuthSwitch prompt="Changed your mind?">
            <Button variant="link" asChild>
              <Link to="/">Back to your workspaces</Link>
            </Button>
          </AuthSwitch>
        ) : (
          <AuthSwitch prompt="Already have an account?">
            <Button variant="link" asChild>
              <Link to="/sign-in">Sign in</Link>
            </Button>
          </AuthSwitch>
        )
      }
    >
      <form onSubmit={submit} className="ws-auth-fields">
        {!signedIn && (
          <>
            <Field label="Your full name">{(id) => <Input id={id} value={name} required autoComplete="name" autoFocus onChange={(e) => setName(e.target.value)} />}</Field>
            <Field label="Work email">{(id) => <Input id={id} type="email" value={email} required autoComplete="email" onChange={(e) => setEmail(e.target.value)} />}</Field>
            <Field label="Create a password" hint="Use at least 8 characters.">
              {(id) => <Input id={id} type="password" value={password} required autoComplete="new-password" onChange={(e) => setPassword(e.target.value)} />}
            </Field>
          </>
        )}
        <Field label="Workspace name">{(id) => <Input id={id} value={workspace} required autoFocus={signedIn} placeholder="Your business or brand" onChange={(e) => setWorkspace(e.target.value)} />}</Field>
        {!signedIn && (
          <label className="ws-checkbox-text">
            <Checkbox checked={agreed} onCheckedChange={(value) => setAgreed(value === true)} />
            <span>I agree to the Terms of Service and Privacy Policy.</span>
          </label>
        )}
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        <Button className="w-full" size="lg" type="submit" disabled={busy}>
          Create workspace
          <ArrowRight />
        </Button>
      </form>
    </AuthLayout>
  )
}
